import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { ClipboardList, UsersRound } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createEvent } from '../../hooks/calendarApi';
import { getEmployees } from '../../hooks/employees';
import { CalendarEvent, Employee } from '../../utils/types';
import {
  CalendarField,
  CalendarHero,
  CalendarPrimaryButton,
  CalendarSecondaryButton,
  CalendarSectionCard,
  CalendarShell,
  CalendarTypeBadge,
  formatCalendarDateTime,
} from './components/calendarUi';

type CreateEventFormValues = {
  title: string;
  description: string;
  start_datetime: string;
  end_datetime: string;
  assigned_to: string;
  status: CalendarEvent['status'];
  event_type: Extract<CalendarEvent['event_type'], 'custom' | 'meeting' | 'deadline'>;
};

const defaultFormValues: CreateEventFormValues = {
  title: '',
  description: '',
  start_datetime: '',
  end_datetime: '',
  assigned_to: '',
  status: 'upcoming',
  event_type: 'custom',
};

export default function CreateEventPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<CreateEventFormValues>(() => {
    const start = searchParams.get('start');
    const end = searchParams.get('end');

    const toLocalInputValue = (value?: string | null) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const day = `${date.getDate()}`.padStart(2, '0');
      const hours = `${date.getHours()}`.padStart(2, '0');
      const minutes = `${date.getMinutes()}`.padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return {
      ...defaultFormValues,
      start_datetime: toLocalInputValue(start),
      end_datetime: toLocalInputValue(end),
    };
  });

  useEffect(() => {
    const loadEmployees = async () => {
      if (!orgId) {
        return;
      }

      setLoadingEmployees(true);
      const response = await getEmployees(orgId);
      if (response?.success && Array.isArray(response.data)) {
        setEmployees(response.data);
      } else {
        setEmployees([]);
      }
      setLoadingEmployees(false);
    };

    loadEmployees();
  }, [orgId]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!orgId) {
      return;
    }

    if (!formData.title.trim() || !formData.start_datetime) {
      toast({
        title: 'Title and start time are required',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.end_datetime && new Date(formData.end_datetime) <= new Date(formData.start_datetime)) {
      toast({
        title: 'End time must be after start time',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const response = await createEvent(orgId, {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: formData.end_datetime
          ? new Date(formData.end_datetime).toISOString()
          : undefined,
        assigned_to: formData.assigned_to || undefined,
        status: 'upcoming',
        event_type: formData.event_type,
      });

      toast({
        title: 'Event created',
        description: `${formData.title} is now on the calendar.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate(`/organization/${orgId}/events/${response.data.id}/view`);
    } catch (error: any) {
      toast({
        title: 'Could not create event',
        description: error?.response?.data?.detail || 'Please review the form and try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <CalendarShell compact>
      <CalendarHero
        eyebrow="New event"
        title="Create a standalone calendar event"
        description="Use this for meetings, deadlines, and custom scheduling items. Task and lead follow-up events should still be created from their source workflows."
        primaryAction={
          <CalendarPrimaryButton
            leftIcon={<AddIcon />}
            onClick={() => formRef.current?.requestSubmit()}
            isLoading={saving}
          >
            Create event
          </CalendarPrimaryButton>
        }
        secondaryAction={
          <CalendarSecondaryButton onClick={() => navigate(`/organization/${orgId}/calendar`)}>
            Cancel
          </CalendarSecondaryButton>
        }
      />

      <form ref={formRef} onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
          <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
            <CalendarSectionCard title="Basics" description="The primary information people need when scanning the schedule.">
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Title</FormLabel>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Quarterly planning review"
                    borderRadius="16px"
                    bg="gray.50"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Add context, agenda, or preparation notes."
                    minH="140px"
                    resize="vertical"
                    borderRadius="16px"
                    bg="gray.50"
                  />
                </FormControl>
              </Stack>
            </CalendarSectionCard>

            <CalendarSectionCard title="Schedule and ownership" description="Set timing, owner, and the event type that best matches the work.">
              <Stack spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Start date and time</FormLabel>
                    <Input
                      type="datetime-local"
                      name="start_datetime"
                      value={formData.start_datetime}
                      onChange={handleChange}
                      borderRadius="16px"
                      bg="gray.50"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>End date and time</FormLabel>
                    <Input
                      type="datetime-local"
                      name="end_datetime"
                      value={formData.end_datetime}
                      onChange={handleChange}
                      borderRadius="16px"
                      bg="gray.50"
                    />
                    <FormHelperText>Leave empty if the event only needs a start time.</FormHelperText>
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Assigned to</FormLabel>
                    <Select
                      name="assigned_to"
                      value={formData.assigned_to}
                      onChange={handleChange}
                      placeholder="No assignee"
                      borderRadius="16px"
                      bg="gray.50"
                      isDisabled={loadingEmployees}
                    >
                      {employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.user?.first_name} {employee.user?.last_name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <CalendarField label="Status" value="Upcoming" />
                </SimpleGrid>

                <FormControl>
                  <FormLabel>Event type</FormLabel>
                  <Select
                    name="event_type"
                    value={formData.event_type}
                    onChange={handleChange}
                    borderRadius="16px"
                    bg="gray.50"
                  >
                    <option value="custom">Custom</option>
                    <option value="meeting">Meeting</option>
                    <option value="deadline">Deadline</option>
                  </Select>
                </FormControl>
              </Stack>
            </CalendarSectionCard>
          </Stack>

          <VStack align="stretch" spacing={6}>
            <CalendarSectionCard title="Event summary" description="A quick read of the record before it is created.">
              <Stack spacing={4}>
                <CalendarField label="Title" value={formData.title || 'Untitled event'} />
                <CalendarField
                  label="Starts"
                  value={formData.start_datetime ? formatCalendarDateTime(new Date(formData.start_datetime).toISOString()) : 'Not set'}
                />
                <CalendarField
                  label="Ends"
                  value={formData.end_datetime ? formatCalendarDateTime(new Date(formData.end_datetime).toISOString()) : 'Not set'}
                />
                <HStack justify="space-between">
                  <Text color="gray.600">Type</Text>
                  <CalendarTypeBadge type={formData.event_type} />
                </HStack>
              </Stack>
            </CalendarSectionCard>

            <CalendarSectionCard title="Workflow-safe creation" description="Use the right entry point for linked schedule items.">
              <VStack align="stretch" spacing={4}>
                <Button
                  variant="outline"
                  borderRadius="16px"
                  leftIcon={<UsersRound size={16} />}
                  onClick={() => navigate(`/organization/${orgId}/leads-dashboard`)}
                >
                  Create lead follow-up from leads
                </Button>
                <Button
                  variant="outline"
                  borderRadius="16px"
                  leftIcon={<ClipboardList size={16} />}
                  onClick={() => navigate(`/organization/${orgId}/tasks/`)}
                >
                  Create task event from tasks
                </Button>
                <Text fontSize="sm" color="gray.500">
                  These linked events stay synced to their source record, which keeps the calendar consistent with the rest of the CRM.
                </Text>
              </VStack>
            </CalendarSectionCard>
          </VStack>
        </SimpleGrid>
      </form>
    </CalendarShell>
  );
}
